import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useDownloadTracker } from '../hooks/useAnalytics';

const steps = [
  { id: 1, title: "Find a Fitness Buddy", desc: "Get matched with a workout partner nearby based on your fitness goals and level.", icon: FiIcons.FiUsers },
  { id: 2, title: "Complete Daily Challenges", desc: "Complete fun,gamified fitness challenges customized for you every day.", icon: FiIcons.FiActivity },
  { id: 3, title: "Earn FitPair Tokens", desc: "Accumulate FitPair tokens for every workout,challenge,and milestone you achieve.", icon: FiIcons.FiAward },
  { id: 4, title: "Redeem Real Rewards", desc: "Exchange your earned tokens for real vouchers from top brands like Nike & Amazon.", icon: FiIcons.FiShoppingBag },
];

const HowItWorks = () => {
  const trackDownload = useDownloadTracker();

  return (
    <section id="how-it-works" className="py-10 sm:py-16 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          className="text-center mb-10 sm:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            How FitPair Fitness Rewards Work
          </h2>
          <p className="text-sm sm:text-base text-gray-500 max-w-2xl mx-auto">
            Simple steps to find a fitness buddy,complete daily challenges,and turn your sweat into real rewards.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6, type: "spring", stiffness: 100 }}
              whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(225,6,0,0.12)", borderColor: "#E10600" }}
              className="bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 shadow-soft transition-all duration-300 group cursor-pointer relative"
            >
              <motion.div
                className="absolute -top-3 -right-3 w-7 h-7 sm:w-8 sm:h-8 bg-brand-red rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ delay: index * 0.15 + 0.3, type: "spring" }}
              >
                {step.id}
              </motion.div>

              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-brand-light rounded-xl flex items-center justify-center text-brand-red text-xl sm:text-2xl mb-5 sm:mb-6 group-hover:bg-brand-red group-hover:text-white transition-all duration-300">
                <SafeIcon icon={step.icon} />
              </div>

              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3 group-hover:text-brand-red transition-colors">
                {step.title}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>

              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-14 -right-4 w-8 h-0.5 bg-gradient-to-r from-brand-red to-transparent" />
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-12 sm:mt-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          <p className="text-gray-900 font-bold mb-6 text-lg">Ready to start earning fitness rewards?</p>
          <motion.a
            href="https://fitpair.app/download"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackDownload('Download Page', 'HowItWorks')}
            whileHover={{ scale: 1.05, boxShadow: "0 15px 30px rgba(225,6,0,0.3)" }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 bg-brand-red text-white px-8 py-4 rounded-full font-black text-lg shadow-glow transition-all"
          >
            <SafeIcon icon={FiIcons.FiDownload} className="text-xl" />
            Download FitPair Free
          </motion.a>

          <div className="mt-8">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-flex items-center gap-2 text-gray-400 font-medium text-xs uppercase tracking-widest"
            >
              <span>Scroll to explore</span>
              <SafeIcon icon={FiIcons.FiArrowDown} />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;